import { useCallback, useState } from 'react';
import { canUseView, viewsFor } from '@/lib/views';
import type { Role } from '@/types/roles';

export const ACTIVE_VIEW_KEY = 'areeb-active-view';

/**
 * Which of their own trees the person is currently looking at.
 *
 * Read back through `canUseView`, never trusted raw. A stored value survives a
 * role change — an admin demoted to recruiter still has "admin" in their
 * browser — and honouring it would render a nav full of destinations every
 * guard will refuse. Falling back to the real role is both correct and the
 * safe direction to fail in.
 */
function readStored(roles: Role[]): Role {
  const fallback = viewsFor(roles)[0];
  try {
    const stored = localStorage.getItem(ACTIVE_VIEW_KEY) as Role | null;
    return stored && canUseView(roles, stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

export function useActiveView(roles: Role[]): [Role, (next: Role) => void] {
  const [view, setView] = useState<Role>(() => readStored(roles));

  const choose = useCallback((next: Role) => {
    if (!canUseView(roles, next)) return;
    setView(next);
    try {
      localStorage.setItem(ACTIVE_VIEW_KEY, next);
    } catch {
      // The choice is lost for this browser; the session still works.
    }
  }, [roles]);

  // Roles that changed under a stored view correct themselves on the next read.
  const available = viewsFor(roles);
  const effective = available.includes(view) ? view : available[0];
  return [effective, available.length > 1 ? choose : () => {}];
}
